/** Explicit social expression, never inferred emotion or a recommendation-learning signal. */
export const REACTIONS = Object.freeze(
  [
    { id: 'like', label: 'Like', emoji: '👍' },
    { id: 'love', label: 'Love', emoji: '❤️' },
    { id: 'care', label: 'Care', emoji: '🤗' },
    { id: 'haha', label: 'Haha', emoji: '😆' },
    { id: 'wow', label: 'Wow', emoji: '😮' },
    { id: 'sad', label: 'Sad', emoji: '😢' },
    { id: 'angry', label: 'Angry', emoji: '😠' },
  ].map(Object.freeze),
);
const available = new Set(REACTIONS.map(({ id }) => id));
/** Normalize legacy likes and accepted reaction records. Unknown persisted values are ignored;
 * canonical reactions override legacy likes. Returns a new actor map without mutating inputs. */
export function reactionMap(record) {
  const entries = new Map(
    (record.likes ?? []).filter((id) => typeof id === 'string').map((id) => [id, 'like']),
  );
  for (const [actor, reaction] of Object.entries(record.reactions ?? {}))
    if (available.has(reaction)) entries.set(actor, reaction);
  return Object.fromEntries(entries);
}
/** Idempotently set one actor's reaction, or explicitly remove with null. Invalid boundary
 * choices fail. Actor identity and object access must be authorized by the calling service. */
export function setReaction(record, actor, reaction) {
  if (typeof actor !== 'string' || !actor) throw new Error('Reaction actor required.');
  if (reaction !== null && !available.has(reaction))
    throw new Error('Choose an available reaction.');
  const reactions = reactionMap(record);
  if (reaction === null) delete reactions[actor];
  else reactions[actor] = reaction;
  return reactions;
}
/** Aggregate only authorized visible actors; reveal the viewer's own choice, never actor maps.
 * Counts are number of people (one per person), not repeated taps. Visibility is injected. */
export function reactionSummary(record, viewer, visibleActor = () => true) {
  const reactions = reactionMap(record);
  const reactionCounts = Object.fromEntries(REACTIONS.map(({ id }) => [id, 0]));
  for (const [actor, reaction] of Object.entries(reactions))
    if (visibleActor(actor)) reactionCounts[reaction]++;
  return {
    reactionCounts,
    myReaction: visibleActor(viewer) ? (reactions[viewer] ?? null) : null,
    totalReactions: Object.values(reactionCounts).reduce((sum, count) => sum + count, 0),
  };
}

/** Validate aggregate response evidence from a trusted same-window unique-view cohort.
 * Counts are people, with one current reaction per person. Returns null for missing
 * or inconsistent denominators; rates are propensity proxies, never mood/satisfaction.
 * Beta(2,18) shrinkage is provisional and does not correct position/selection bias. */
export function reactionRates(input) {
  if (
    !input ||
    !Number.isSafeInteger(input.uniqueViewers) ||
    input.uniqueViewers <= 0 ||
    !input.counts ||
    typeof input.counts !== 'object' ||
    Array.isArray(input.counts)
  )
    return null;
  const keys = REACTIONS.map(({ id }) => id);
  if (Object.keys(input.counts).some((key) => !keys.includes(key))) return null;
  const counts = keys.map((key) => (Object.hasOwn(input.counts, key) ? input.counts[key] : 0));
  if (counts.some((count) => !Number.isSafeInteger(count) || count < 0)) return null;
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (!Number.isSafeInteger(total) || total > input.uniqueViewers) return null;
  return {
    response: (total + 2) / (input.uniqueViewers + 20),
    byType: Object.fromEntries(
      keys.map((key, index) => [
        key,
        (counts[index] + 2 / keys.length) / (input.uniqueViewers + 20),
      ]),
    ),
  };
}
